import { useEffect, useRef, useState } from "react";
import scss from "../FormWrapper.module.scss";
import styles from "./FormInput.module.scss";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import useAPI from "hooks/useAPI";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { toast } from "react-toastify";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { srLatn } from "date-fns/locale";
import { blobToData } from "helpers/data";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { ListSubheader, Menu, MenuItem } from "@mui/material";

const normalizeSelectText = (value) =>
    String(value ?? "")
        .toLocaleLowerCase("sr")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/đ/g, "dj");

export const InputWrapper = ({ children = null, label, required, disabled, margin = "dense", error = null, fullWidth = true, styleFormControl, columns }) => {
    const { t } = useTranslation();
    return (
        <FormControl fullWidth={fullWidth} margin={margin} error={error !== null} sx={styleFormControl} className={`col-sm-${columns ? (columns === true ? 6 : columns) : 12}`}>
            <FormLabel required={required} disabled={disabled} sx={{ fontFamily: "Montserrat", color: "var(--text-color)" }}>
                {t(label)}
            </FormLabel>
            {children}
        </FormControl>
    );
};

export const FormWrapper = ({ label, field, required, columns, children, className }) => {
    const { t } = useTranslation();
    return (
        <div key={field} className={`${className} ${scss.input} col-sm-${columns ? (columns === true ? 6 : columns) : 12}`}>
            {/* Optional label */}
            {label != null && (
                <label htmlFor={field} className={required ? scss.required : ""}>
                    {label && t(label) + ":"}
                </label>
            )}

            {children}
        </div>
    );
};

export const FormInput = ({ error, label, data, setData, field, placeholder, type = "text", rows, required, disabled, columns, className, helperText }) => {
    const { t } = useTranslation();
    return (
        <FormWrapper label={t(label)} required={required} columns={columns} className={className}>
            {type !== "textarea" ? (
                <input
                    id={field}
                    type={type}
                    className={error ? scss["has-error"] : undefined}
                    style={{
                        fontSize: "16px",
                    }}
                    value={data[field] ?? ""}
                    placeholder={placeholder}
                    onChange={(event) => setData((data) => ({ ...data, [field]: type !== "checkbox" ? event.target.value : event.target.checked }))}
                    disabled={disabled}
                />
            ) : (
                <textarea
                    id={field}
                    className={error ? scss["has-error"] : undefined}
                    style={{
                        fontSize: "16px",
                    }}
                    rows={rows}
                    value={data[field] ?? ""}
                    placeholder={placeholder}
                    onChange={(event) => setData((data) => ({ ...data, [field]: event.target.value }))}
                    disabled={disabled}
                />
            )}
            {error && <p className={scss["error-text"]}>{error}</p>}
            {helperText ? <p style={{ margin: "0", fontSize: "0.75rem" }}>{t(helperText)}</p> : null}
        </FormWrapper>
    );
};

export const FormSelect = ({ className, label, data, setData, field, options = [], required, disabled, columns, updateCountryId, setUpdateCountryId, error }) => {
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState(null);
    const [search, setSearch] = useState("");
    const selectedOption = options.find((option) => String(option?.id ?? option?.[0]) === String(data[field] ?? ""));
    const searchable = options.length > 8;
    const filteredOptions = searchable && search
        ? options.filter((option) => normalizeSelectText(option?.name ?? option?.[1]).includes(normalizeSelectText(search)))
        : options;

    return (
        <FormWrapper label={label ? t(label) : null} field={field} required={required} columns={columns}>
            <button
                type="button"
                className={`${styles.select} ${error ? scss["has-error"] : ""}`}
                style={className}
                id={field}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchor)}
                onClick={(event) => {
                    setSearch("");
                    setAnchor(event.currentTarget);
                }}
                disabled={disabled}
            >
                <span className={styles.selectValue}>{selectedOption?.name ?? selectedOption?.[1] ?? ""}</span>
                <svg className={styles.chevron} viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                autoFocus={!searchable}
                variant="menu"
                PaperProps={{ className: styles.selectMenu, style: { width: anchor?.offsetWidth, maxWidth: anchor?.offsetWidth } }}
                MenuListProps={{ "aria-label": label ? t(label) : field }}
            >
                {searchable && (
                    <ListSubheader className={styles.selectSearch}>
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <circle cx="8.75" cy="8.75" r="5.75" stroke="currentColor" strokeWidth="1.6" />
                            <path d="m13 13 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                        <input
                            type="search"
                            autoFocus
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key !== "Escape") event.stopPropagation();
                                if (event.key === "Enter") event.preventDefault();
                            }}
                            placeholder="Pretražite opcije"
                            aria-label="Pretražite opcije"
                        />
                    </ListSubheader>
                )}
                {filteredOptions.length === 0 && <MenuItem disabled className={styles.selectOption}>Nema rezultata</MenuItem>}
                {filteredOptions.map((option, index) => {
                    const value = String(option?.id ?? option?.[0] ?? "");
                    return (
                        <MenuItem
                            key={value || index}
                            className={styles.selectOption}
                            selected={value === String(data[field] ?? "")}
                            onClick={() => {
                                setData((data) => ({ ...data, [field]: value }));
                                if (field === "id_country" && setUpdateCountryId) setUpdateCountryId(value);
                                setAnchor(null);
                            }}
                        >
                            {option?.name ?? option?.[1]}
                        </MenuItem>
                    );
                })}
            </Menu>
            {error && <p className={scss["error-text"]}>{error}</p>}
        </FormWrapper>
    );
};

export const FormSelectSearch = ({ label, data, setData, field, options = [], required, disabled, columns }) => {
    const [value, setValue] = useState("");
    const change = (evt) => {
        setValue(evt.target.value);
    };
    const { t } = useTranslation();
    return (
        <FormWrapper label={t(label)} required={required} columns={columns}>
            <input list={field} disabled={disabled} value={data[field] ?? ""} onChange={change} />
            <datalist
                id={field}
                onChange={(event) => {
                    setData((data) => ({ ...data, [field]: event.target.value }));
                    // label === "Država" && setUpdateCountryId(event.target.value);
                }}
            >
                {options.map((option) => (
                    <option key={option?.id ?? option[0]} value={option?.name ?? option[1]} />
                ))}
            </datalist>
        </FormWrapper>
    );
};

export const AutocompleteInput = ({
    label,
    required,
    disabled,
    error = null,
    name,
    value,
    margin = "dense",
    onChange = () => {},
    description,
    fillFromApi,
    usePropName,
    options,
    queryString = "",
    optionsIsEmpty = () => {},
    styleFormControl,
    columns,
    className,
    menuClassName,
}) => {
    const { t } = useTranslation();
    const api = useAPI();
    const [opt, setOpt] = useState([]);
    const [myValue, setMyValue] = useState(null);
    const filter = createFilterOptions();
    useEffect(() => {
        let isMounted = true;
        let path = usePropName ? `${fillFromApi}/${name}?${queryString}` : `${fillFromApi}?${queryString}`;
        const fillDdl = async () => {
            await api
                .get(path, false)
                .then((response) => {
                    if (isMounted) {
                        const { payload } = response;
                        setOpt(payload);
                    }
                })
                .catch((error) => {
                    console.warn(error);
                });
        };

        if (fillFromApi) {
            fillDdl();
        }

        return () => {
            isMounted = false;
        };
    }, [fillFromApi, queryString]);

    return (
        // <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleFormControl} columns={columns}>
        <FormWrapper label={t(label)} required={required} columns={columns} className={className}>
            <Autocomplete
                classes={menuClassName ? { paper: menuClassName } : undefined}
                disabled={disabled}
                value={value}
                onChange={(event, newInputValue) => {
                    let newIval = newInputValue ? newInputValue : "";
                    setMyValue(newIval);
                    if (opt.length > 0) {
                        let selectedOption = opt.find((o) => o.name === newInputValue);
                        if (selectedOption) {
                            newIval = selectedOption.id;
                        }
                    }
                    onChange(newIval);
                }}
                options={options?.length > 0 ? options : opt}
                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;
                    // Suggest the creation of a new value
                    const isExisting = options.some((option) => inputValue === option.name);
                    if (inputValue !== "" && !isExisting) {
                        filtered.push({
                            inputValue,
                            name: `Dodaj: "${inputValue}"`,
                        });
                    }
                    return filtered;
                }}
                getOptionLabel={(option) => {
                    // Value selected with enter, right from the input
                    if (typeof option === "string") {
                        return option;
                    }
                    // Add "xxx" option created dynamically
                    if (option.inputValue) {
                        return option.inputValue;
                    }
                    // Regular option
                    return option.name;
                }}
                sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0, border: 0 },
                    ".MuiInputBase-root": { padding: "0!important" },
                    ".MuiAutocomplete-input": { padding: "0.5rem 1rem!important", fontFamily: "Montserrat" },
                    ".MuiSvgIcon-root": { fill: "inherit" },
                    ".MuiOutlinedInput-notchedOutline": "none",
                    "&.MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--primary-color)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "var(--primary-color)",
                        borderRadius: "0.625rem",
                    },
                }}
                renderInput={(params, param) => (
                    <TextField
                        {...params}
                        onChange={(e) => {
                            onChange(e.target.value);
                        }}
                    />
                )}
                freeSolo={true}
            />
            <FormHelperText sx={{ fontFamily: "Montserrat" }}>{error ? error : t(description)}</FormHelperText>
        </FormWrapper>
    );
};

export const InputCheckbox = ({
    label,
    required,
    disabled,
    name,
    value,
    error = null,
    margin = "dense",
    onChange = () => null,
    description,
    labelStyle,
    styleCheckbox,
    columns,
    styleCheckBoxWrapp,
    className,
}) => {
    const { t } = useTranslation();
    return (
        // <InputWrapper required={required} disabled={disabled} margin={margin} error={error} styleFormControl={styleCheckBoxWrapp}>
        <FormWrapper required={required} columns={columns} className={className}>
            <FormControlLabel
                control={<Checkbox name={name} checked={value} onChange={onChange} disabled={disabled} sx={styleCheckbox} />}
                label={t(label)}
                sx={{
                    ".MuiTypography-root": { fontFamily: "Montserrat", fontSize: "0.9rem", lineHeight: "normal" },
                    ".MuiSvgIcon-root": { width: "1.1rem", height: "1.1rem", fill: "var(--primary-color)" },
                    ...labelStyle,
                }}
            />
            <FormHelperText>{error ? error : description}</FormHelperText>
        </FormWrapper>
    );
};

export const FilePicker = ({
    label,
    required,
    disabled,
    error = null,
    onFilePicked = () => {},
    onFilesPicked,
    description,
    selectedFile,
    columns,
    className,
    multipleFileSelection = false,
    handleRemoveFile = () => {},
}) => {
    const ref = useRef();
    const { t } = useTranslation();

    const handleChange = async (event) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = "";
        if (!files.length) return;

        const allowedExtensions = new Set(["mp4", "mov", "avi", "wmv", "flv", "mkv", "webm", "txt", "pdf", "png", "jpg", "jpeg", "gif", "doc", "docx", "xls", "xlsx", "zip", "rar", "mp3", "ogg"]);
        const validFiles = files.filter((file) => {
            if (!allowedExtensions.has(file.name.split(".").pop().toLowerCase())) {
                toast.error(t("global-file_picker-wrong_file_type"));
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error(t("global-file_picker-maximum_allowed_size"));
                return false;
            }
            return true;
        });

        if (!validFiles.length) return;
        const pickedFiles = await Promise.all(validFiles.map(async (file) => ({ base_64: await blobToData(file), name: file.name })));
        if (multipleFileSelection && onFilesPicked) onFilesPicked(pickedFiles);
        else pickedFiles.forEach(onFilePicked);
    };

    const filesToShow = multipleFileSelection ? selectedFile ?? [] : selectedFile ? [selectedFile] : [];

    return (
        <FormWrapper label={t(label)} required={required} columns={columns} className={className}>
            <input ref={ref} type="file" className={styles.filePickerInput} onChange={handleChange} disabled={disabled} multiple={multipleFileSelection} tabIndex={-1} />
            <div className={styles.filePickerRow}>
                <button type="button" className={styles.filePickerButton} onClick={() => ref.current?.click()} disabled={disabled}>
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M10 13V3m0 0L6.5 6.5M10 3l3.5 3.5M4 12.5v3A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5v-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Dodaj prilog
                </button>
                <span className={styles.filePickerHint}>Do 5 MB po fajlu</span>
            </div>
            {filesToShow.length > 0 && (
                <div className={styles.filePickerFiles} aria-label={t("global-file_picker-selected_files")}>
                    {filesToShow.map((file, index) => (
                        <span key={`${file.name}-${index}`} className={styles.filePickerFile}>
                            <span>{file.name}</span>
                            {multipleFileSelection && (
                                <button type="button" aria-label={`Ukloni fajl ${file.name}`} onClick={() => handleRemoveFile(file)}>
                                    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            <FormHelperText>{error ? error : description}</FormHelperText>
        </FormWrapper>
    );
};

export const InputDateTime = ({ label, required, disabled, error = null, name, value, margin = "dense", onChange = () => null, description, columns, className, openPickerIcon }) => {
    const [timeOpen, setTimeOpen] = useState(false);
    const timePickerRef = useRef(null);
    const hoursListRef = useRef(null);
    const minutesListRef = useRef(null);
    const parsedValue = value ? new Date(value) : null;
    const dateValue = parsedValue && !Number.isNaN(parsedValue.getTime()) ? parsedValue : null;

    const handleChange = (newValue) => {
        const ev = {
            target: {
                name: name,
                value: newValue,
            },
        };
        onChange(ev, "date_time");
    };

    const handleDateChange = (selectedDate) => {
        if (!selectedDate) {
            handleChange(null);
            return;
        }
        if (Number.isNaN(selectedDate.getTime())) return;

        const nextValue = new Date(selectedDate);
        nextValue.setHours(dateValue?.getHours() ?? 0, dateValue?.getMinutes() ?? 0, 0, 0);
        handleChange(nextValue);
    };

    const handleTimeChange = (part, selectedValue) => {
        if (!dateValue) return;
        const nextValue = new Date(dateValue);
        nextValue.setHours(part === "hours" ? selectedValue : dateValue.getHours(), part === "minutes" ? selectedValue : dateValue.getMinutes(), 0, 0);
        handleChange(nextValue);
    };

    const timeValue = dateValue ? `${String(dateValue.getHours()).padStart(2, "0")}:${String(dateValue.getMinutes()).padStart(2, "0")}` : "";

    useEffect(() => {
        if (!timeOpen) return;

        const closeOnOutsideClick = (event) => {
            if (!timePickerRef.current?.contains(event.target)) setTimeOpen(false);
        };
        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                setTimeOpen(false);
                timePickerRef.current?.querySelector("button")?.focus();
            }
        };

        document.addEventListener("pointerdown", closeOnOutsideClick);
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("pointerdown", closeOnOutsideClick);
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [timeOpen]);

    useEffect(() => {
        if (!timeOpen || !dateValue) return;
        [hoursListRef.current, minutesListRef.current].forEach((list) => {
            const selected = list?.querySelector('[aria-selected="true"]');
            if (selected) list.scrollTop = selected.offsetTop - list.offsetTop - (list.clientHeight - selected.clientHeight) / 2;
        });
    }, [timeOpen]);

    return (
        // <InputWrapper label={label} required={required} disabled={disabled} margin={margin} error={error} columns={columns}>
        <FormWrapper label={label} required={required} columns={columns} className={className}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={srLatn}>
                <div className={styles.dateTimeFields}>
                    <DatePicker
                        slots={openPickerIcon ? { openPickerIcon } : undefined}
                        slotProps={{ popper: { className: styles.datePickerPopper } }}
                        value={dateValue}
                        onChange={handleDateChange}
                        disabled={disabled}
                        format="dd.MM.yyyy"
                        sx={{ flex: 1, minWidth: 0, ".MuiInputBase-root": { fontFamily: "Montserrat" } }}
                    />
                    <div className={styles.dateTimeClockWrapper} ref={timePickerRef}>
                        <button
                            type="button"
                            aria-label={`Vreme kupovine${timeValue ? `: ${timeValue}` : ""}`}
                            aria-haspopup="true"
                            aria-expanded={timeOpen}
                            className={styles.dateTimeClock}
                            onClick={() => setTimeOpen((open) => !open)}
                            disabled={disabled || !dateValue}
                        >
                            <span>{timeValue || "--:--"}</span>
                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        {timeOpen && dateValue && (
                            <div className={styles.dateTimeMenu} role="group" aria-label="Izbor vremena">
                                <div className={styles.dateTimeColumn}>
                                    <span className={styles.dateTimeColumnLabel}>Sati</span>
                                    <div className={styles.dateTimeOptions} role="listbox" aria-label="Sati" ref={hoursListRef}>
                                        {Array.from({ length: 24 }, (_, hour) => (
                                            <button key={hour} type="button" role="option" aria-selected={dateValue.getHours() === hour} onClick={() => handleTimeChange("hours", hour)}>
                                                {String(hour).padStart(2, "0")}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className={styles.dateTimeColumn}>
                                    <span className={styles.dateTimeColumnLabel}>Minuti</span>
                                    <div className={styles.dateTimeOptions} role="listbox" aria-label="Minuti" ref={minutesListRef}>
                                        {Array.from({ length: 60 }, (_, minute) => (
                                            <button key={minute} type="button" role="option" aria-selected={dateValue.getMinutes() === minute} onClick={() => handleTimeChange("minutes", minute)}>
                                                {String(minute).padStart(2, "0")}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </LocalizationProvider>
            <FormHelperText>{error ? error : description}</FormHelperText>
        </FormWrapper>
    );
};
